// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { casesApi } from '../api/cases';
import { CasesBar, CasesBarControls, type CasesBarControlsProps } from '../components/sager/CasesBar';
import { Spinner } from '../components/Spinner';
import { AdminUsersPage } from '../routes/AdminUsersPage';
import { KontoPage } from '../routes/KontoPage';
import { SagerPage } from '../routes/SagerPage';
import { GroupsPage } from '../routes/GroupsPage';
import { useAuth } from './AuthGate';
import { ADMIN_USERS_PATH, CASES_PATH, KONTO_PATH, legacyRedirectTarget, readLastCase, GROUPS_PATH } from './cases';
import { useTheme } from './useTheme';
import styles from './AppShell.module.css';

/**
 * Everything outside a case: the case list at `/sager` and the account-level
 * pages (`/konto`, …) — the floating CasesBar over the account pages (Sager
 * puts its controls in its own header), no sidebar, no events socket (those
 * belong to a case).
 */
export function CasesApp() {
  const theme = useTheme();
  const auth = useAuth();
  const { pathname } = useLocation();
  const bare = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const controls: CasesBarControlsProps = {
    themePreference: theme.preference,
    onThemeChange: theme.setPreference,
    user: auth?.me.mode === 'server' ? auth.me.user : undefined,
    onLogout: auth ? () => void auth.logout() : undefined,
    viaShareLink: auth?.me.via === 'share_link',
  };
  return (
    <div className={styles.shell}>
      <div className={styles.body}>
        <main className={styles.content} tabIndex={-1}>
          {/* Inside <main> so it can stick while the page scrolls under it.
              Sager has none: its header carries the wordmark and the controls. */}
          {bare !== CASES_PATH && <CasesBar {...controls} />}
          <Routes>
            <Route path={CASES_PATH} element={<SagerPage controls={<CasesBarControls {...controls} />} />} />
            <Route path={KONTO_PATH} element={<KontoPage />} />
            <Route path={GROUPS_PATH} element={<GroupsPage />} />
            <Route path={ADMIN_USERS_PATH} element={<AdminUsersPage />} />
            <Route path="*" element={<Navigate to={CASES_PATH} replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

/**
 * An old, unprefixed path (a bookmark from before cases): on to the same
 * place in the last-used case if the server still has it, else to the list.
 */
export function LegacyRedirect({ pathname, search }: { pathname: string; search: string }) {
  useEffect(() => {
    const controller = new AbortController();
    casesApi
      .list(controller.signal)
      .then(
        (list) =>
          legacyRedirectTarget(
            pathname,
            search,
            readLastCase(),
            list.cases.map((c) => c.id),
          ),
        () => CASES_PATH,
      )
      .then((target) => {
        if (!controller.signal.aborted) window.location.replace(target);
      });
    return () => controller.abort();
  }, [pathname, search]);
  return <Spinner label="Finder sagen…" />;
}
